import { useLayoutEffect, useRef } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { resolveColumnMeta } from "../columns/columnKinds";
import type { DataGridTable } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { scheduleOverflowEdges } from "../grid/useViewportObservers";
import { getColumnSizeBounds, readCellPadding } from "./cellPadding";
import { columnAfterVar, columnStartVar, columnWidthVar } from "./columnVars";
import type { DataGridColumnLayout } from "../gridOptionTypes";
import { computeColumnWidths, getColumnsInset } from "./fillColumns";
import { getStickyPins } from "./stickyPins";

interface ColumnSizeVarsProps<TData extends RowData> {
    table: DataGridTable<TData>;
    controller: GridController;
    rootElement: HTMLDivElement | null;
    columnLayout: DataGridColumnLayout;
}

const px = (value: number) => `${value}px`;

/**
 * Writes column widths and sticky offsets as CSS variables on the root:
 * `--dg-col-<id>-w`, `--dg-col-<id>-start` (start pins), `--dg-col-<id>-after`
 * (end pins), plus `--dg-total-w`, `--dg-pin-start-w`, `--dg-pin-end-w`, and
 * `--dg-viewport-w`. Pinned sides that would crowd out the scrolling
 * columns get `auto` offsets and a `data-unstuck-pins` side on the root, so
 * they scroll with the row. `columnLayout: "center"` adds
 * `--dg-columns-inset`, which rows take as inline padding on both sides.
 * The only component that subscribes to column sizing, so a resize drag
 * re-renders this (and the dragged handle) and nothing else. Renders nothing.
 */
export const ColumnSizeVars = <TData extends RowData>({
    table,
    controller,
    rootElement,
    columnLayout,
}: Readonly<ColumnSizeVarsProps<TData>>) => {
    const sizing = useSelector(table.atoms.columnSizing);
    const visibility = useSelector(table.atoms.columnVisibility);
    const order = useSelector(table.atoms.columnOrder);
    const pinning = useSelector(table.atoms.columnPinning);
    const grouping = useSelector(table.atoms.grouping);
    const containerWidth = useSelector(controller.containerWidth);
    const columnDefs = table.options.columns;
    // Column geometry only: `table` changes identity with every root state
    // change (search, sort), and each effect run writes every var, which
    // restyles the whole grid.
    const { starts, centers, ends } = useMemoByKeys(
        () => ({
            starts: table.getStartVisibleLeafColumns(),
            centers: table.getCenterVisibleLeafColumns(),
            ends: table.getEndVisibleLeafColumns(),
        }),
        [columnDefs, grouping, order, pinning, visibility],
    );
    const writtenRef = useRef(new Set<string>());
    const paddingRef = useRef<{ root: HTMLElement; padding: number } | null>(
        null,
    );

    useLayoutEffect(() => {
        if (!rootElement) {
            return;
        }
        const columns = [...starts, ...centers, ...ends];
        const hasContentWidth = columns.some(
            (column) => column.columnDef.meta?.contentWidth !== undefined,
        );
        if (hasContentWidth && paddingRef.current?.root !== rootElement) {
            const padding = readCellPadding(rootElement);
            paddingRef.current =
                padding === undefined ? null : { root: rootElement, padding };
        }
        const cellPadding =
            paddingRef.current?.root === rootElement
                ? paddingRef.current.padding
                : undefined;
        const getFill = (column: (typeof columns)[number]) => {
            const meta = resolveColumnMeta(column.columnDef, column.id);
            const isFixed =
                column.id in sizing ||
                (column.getIsPinned() !== false && !meta.fillUntilResized);
            return isFixed ? undefined : meta.fill;
        };
        const widths = computeColumnWidths({
            containerWidth,
            columns: columns.map((column) => ({
                id: column.id,
                ...getColumnSizeBounds({ column, cellPadding, sizing }),
                fill: getFill(column),
            })),
        });

        const sumWidths = (sides: typeof columns) =>
            sides.reduce((sum, column) => sum + widths[column.id], 0);
        const { isStartSticky, isEndSticky } = getStickyPins({
            containerWidth,
            pinnedStartWidth: sumWidths(starts),
            pinnedEndWidth: sumWidths(ends),
            centerWidths: centers.map((column) => widths[column.id]),
        });

        const written = new Set<string>();
        const writeValue = (name: string, value: string) => {
            rootElement.style.setProperty(name, value);
            written.add(name);
        };
        const write = (name: string, value: number) =>
            writeValue(name, px(value));
        let pinnedStartWidth = 0;
        for (const column of starts) {
            writeValue(
                columnStartVar(column.id),
                isStartSticky ? px(pinnedStartWidth) : "auto",
            );
            pinnedStartWidth += isStartSticky ? widths[column.id] : 0;
        }
        let pinnedEndWidth = 0;
        for (const column of [...ends].reverse()) {
            writeValue(
                columnAfterVar(column.id),
                isEndSticky ? px(pinnedEndWidth) : "auto",
            );
            pinnedEndWidth += isEndSticky ? widths[column.id] : 0;
        }
        let totalWidth = 0;
        for (const column of columns) {
            write(columnWidthVar(column.id), widths[column.id]);
            totalWidth += widths[column.id];
        }
        write("--dg-total-w", totalWidth);
        write("--dg-pin-start-w", pinnedStartWidth);
        write("--dg-pin-end-w", pinnedEndWidth);
        write("--dg-viewport-w", containerWidth);
        if (columnLayout === "center") {
            write(
                "--dg-columns-inset",
                getColumnsInset({ containerWidth, totalWidth }),
            );
        }
        const unstuckSides = [
            isStartSticky ? undefined : "start",
            isEndSticky ? undefined : "end",
        ].filter((side) => side !== undefined);
        if (unstuckSides.length > 0) {
            rootElement.setAttribute(
                "data-unstuck-pins",
                unstuckSides.join(" "),
            );
        } else {
            rootElement.removeAttribute("data-unstuck-pins");
        }
        for (const name of writtenRef.current) {
            if (!written.has(name)) {
                rootElement.style.removeProperty(name);
            }
        }
        writtenRef.current = written;
        controller.setLayout({ widths, pinnedStartWidth, pinnedEndWidth });
        if (controller.viewport) {
            scheduleOverflowEdges(controller.viewport);
        }
    }, [
        centers,
        columnLayout,
        containerWidth,
        controller,
        ends,
        rootElement,
        sizing,
        starts,
    ]);

    return null;
};
